import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IApiRequest, IRequirementResponse } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";

const GET_REQUIREMENTS_UUID = createUUID();
const GET_RECENT_REQUIREMENTS_UUID = createUUID();

export const useRequirements = () => {
  const [requirements, setRequirements] = useState<IRequirementResponse[]>([]);
  const [recentRequirements, setRecentRequirements] = useState<
    IRequirementResponse[]
  >([]);
  const [refresh, setRefresh] = useState(false);

  const stateManager = useComponentState();

  const getRequirements = async ({
    top,
    skip,
    search = "",
  }: IApiRequest = {}) => {
    stateManager.abort(GET_REQUIREMENTS_UUID);

    const response = await stateManager.run(async () => {
      return homeService.requirements.getAll({
        top,
        skip,
        sort: "name asc",
        filters: { name: { contains: search } },
      });
    }, GET_REQUIREMENTS_UUID);
    if (response.status === HTTP_STATUSES.OK) {
      setRequirements(response.data);
    }
    return response;
  };

  const getRecentRequirements = async ({
    sort,
    filters,
    top,
    search = "",
  }: IApiRequest = {}) => {
    stateManager.abort(GET_RECENT_REQUIREMENTS_UUID);

    const response = await stateManager.run(async () => {
      return homeService.requirements.getAll({
        sort,
        top,
        filters: {
          ...filters,
          name: { contains: search },
        },
      });
    }, GET_RECENT_REQUIREMENTS_UUID);
    if (response.status === HTTP_STATUSES.OK) {
      setRecentRequirements(response.data);
    }
    return response;
  };

  const createRequirement = (data: Partial<IRequirementResponse>) => {
    return stateManager.run(() => {
      return homeService.requirements.create({
        data,
        toastMessageConfig: {
          success: { text: `Successfully created Requirement: ${data.name}.` },
        },
      });
    });
  };

  const updateRequirement = (data: Partial<IRequirementResponse>) => {
    return stateManager.run(() => {
      return homeService.requirements.update({
        id: data.id,
        data,
        toastMessageConfig: {
          success: { text: `Successfully updated Requirement: ${data.name}.` },
        },
      });
    });
  };

  const deleteRequirement = (data: Partial<IRequirementResponse>) => {
    stateManager.run(async () => {
      const response = await homeService.requirements.delete({
        id: data.id,
        data,
        toastMessageConfig: {
          success: { text: `${data.name} was deleted successfully.` },
          failure: { text: `Failed to delete ${data.name}.` },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    requirements,
    recentRequirements,
    refresh,
    setRequirements,
    setRecentRequirements,
    getRequirements,
    getRecentRequirements,
    createRequirement,
    updateRequirement,
    deleteRequirement,
  };
};
